import React, { useState, useEffect, useCallback } from 'react';
import { heroSliderAPI } from '../../services/api';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

const HeroSliderManager = () => {
    const [sliders, setSliders] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ title: '', subtitle: '', buttonText: '', buttonLink: '', order: 0, isActive: true });
    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadSliders = useCallback(async () => {
        try {
            const response = await heroSliderAPI.getAll();
            setSliders(response.data);
        } catch (error) {
            toast.error('Failed to load sliders');
        }
    }, []);

    useEffect(() => { loadSliders(); }, [loadSliders]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const data = new FormData();
        Object.keys(formData).forEach(key => data.append(key, formData[key]));
        if (imageFile) data.append('image', imageFile);

        // client-side validation
        const validate = () => {
            if (!formData.title || formData.title.trim().length < 2) return 'Title is required (min 2 chars)';
            if (!editingId && !imageFile) return 'Image is required when creating a new slider';
            if (imageFile) {
                if (!imageFile.type.startsWith('image/')) return 'Uploaded file must be an image';
                if (imageFile.size > 5 * 1024 * 1024) return 'Image must be smaller than 5MB';
            }
            if (isNaN(Number(formData.order)) || Number(formData.order) < 0) return 'Order must be a non-negative number';
            return null;
        };
        const validationError = validate();
        if (validationError) {
            toast.error(validationError);
            setLoading(false);
            return;
        }

        try {
            if (editingId) {
                await heroSliderAPI.update(editingId, data);
                toast.success('Slider updated successfully');
            } else {
                await heroSliderAPI.create(data);
                toast.success('Slider created successfully');
            }
            resetForm();
            loadSliders();
        } catch (error) {
            toast.error('Operation failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };
    
    const handleEdit = (slider) => {
        setEditingId(slider.id);
        setFormData({
            title: slider.title,
            subtitle: slider.subtitle || '',
            buttonText: slider.buttonText || '',
            buttonLink: slider.buttonLink || '',
            order: slider.order,
            isActive: slider.isActive
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this slider?')) {
            try {
                await heroSliderAPI.delete(id);
                toast.success('Slider deleted successfully');
                loadSliders();
            } catch (error) {
                toast.error('Failed to delete slider');
            }
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setFormData({ title: '', subtitle: '', buttonText: '', buttonLink: '', order: 0, isActive: true });
        setImageFile(null);
        setShowForm(false);
    };

    return (
        <div className="p-6">
            <Toaster position="top-center" />
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Hero Slider Management</h1>
                <button onClick={() => setShowForm(!showForm)} className={`px-4 py-2 rounded-md font-semibold text-white ${showForm ? 'bg-red-500' : 'bg-orange-600'}`}>
                    {showForm ? 'Cancel' : '+ Add New Slider'}
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8 space-y-4">
                    <h3 className="text-xl font-semibold">{editingId ? 'Edit Slider' : 'Add New Slider'}</h3>
                    <input type="text" placeholder="Title *" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="w-full p-2 border rounded"/>
                    <input type="text" placeholder="Subtitle" value={formData.subtitle} onChange={(e) => setFormData({...formData, subtitle: e.target.value})} className="w-full p-2 border rounded"/>
                    <input type="text" placeholder="Button Text" value={formData.buttonText} onChange={(e) => setFormData({...formData, buttonText: e.target.value})} className="w-full p-2 border rounded"/>
                    <input type="text" placeholder="Button Link" value={formData.buttonLink} onChange={(e) => setFormData({...formData, buttonLink: e.target.value})} className="w-full p-2 border rounded"/>
                    <input type="file" onChange={(e) => setImageFile(e.target.files[0])} className="w-full"/>
                    <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-400">{loading ? 'Saving...' : 'Save Slider'}</button>
                </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sliders.map((slider) => (
                    <div key={slider.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                        <img src={`${process.env.REACT_APP_API_URL}${slider.imageUrl}`} alt={slider.title} className="w-full h-48 object-cover"/>
                        <div className="p-4">
                            <h3 className="text-lg font-bold">{slider.title}</h3>
                            <p className="text-gray-600 text-sm">{slider.subtitle}</p>
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full mt-2 inline-block ${slider.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {slider.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <div className="mt-4 flex gap-2">
                                <button onClick={() => handleEdit(slider)} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Edit</button>
                                <button onClick={() => handleDelete(slider.id)} className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
export default HeroSliderManager;